import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    ArrowDownward as ArrowDownwardIcon,
    ArrowUpward as ArrowUpwardIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import {
    getGlobalConfig,
    importSchemaMapperPriorities,
    updateSchemaMapperPriorities,
} from '../../services/api';

function normalizeLevels(levels = []) {
    return (levels || []).map((level, index) => {
        const seen = new Set();
        const rawEntries = Array.isArray(level?.table_entries) ? level.table_entries : [];
        const tableEntries = [];
        const tables = (level?.tables || []).reduce((acc, table) => {
            const value = String(table || '').trim();
            if (!value) return acc;
            const key = value.toLowerCase();
            if (seen.has(key)) return acc;
            seen.add(key);
            acc.push(value);
            return acc;
        }, []);
        rawEntries.forEach((entry) => {
            const tableName = String(entry?.table_name || '').trim();
            if (!tableName) return;
            const key = tableName.toLowerCase();
            if (tableEntries.some((item) => String(item?.table_name || '').trim().toLowerCase() === key)) return;
            tableEntries.push({
                table_name: tableName,
                theme: String(entry?.theme || '').trim(),
            });
            if (!seen.has(key)) {
                seen.add(key);
                tables.push(tableName);
            }
        });
        const numericPriority = Number(level?.priority);
        return {
            name: String(level?.name || '').trim() || `Level ${index + 1}`,
            tables,
            table_entries: tableEntries,
            priority: Number.isFinite(numericPriority) ? numericPriority : null,
        };
    });
}

function normalizeConfig(config) {
    return {
        description: config?.description || 'Priority tables are evaluated level by level for Schema Mapper results, independently for L2 and L3.',
        l2: {
            levels: normalizeLevels(config?.l2?.levels || []),
        },
        l3: {
            levels: normalizeLevels(config?.l3?.levels || []),
        },
    };
}

function sortLevelsByPriority(levels = [], sortOrder = 'desc') {
    const prioritized = [];
    const unprioritized = [];

    (levels || []).forEach((level, index) => {
        if (typeof level?.priority === 'number' && Number.isFinite(level.priority)) {
            prioritized.push({ ...level, _originalIndex: index });
            return;
        }
        unprioritized.push({ ...level, _originalIndex: index });
    });

    if (!prioritized.length) {
        return levels;
    }

    prioritized.sort((left, right) => {
        const delta = sortOrder === 'asc'
            ? left.priority - right.priority
            : right.priority - left.priority;
        if (delta !== 0) return delta;
        return left._originalIndex - right._originalIndex;
    });

    return [...prioritized, ...unprioritized].map(({ _originalIndex, ...level }, index) => ({
        ...level,
        name: /^Level \d+$/i.test(String(level?.name || '').trim())
            ? `Level ${index + 1}`
            : level.name,
    }));
}

function countTables(levels = []) {
    return (levels || []).reduce((total, level) => total + (level?.tables?.length || 0), 0);
}

function getDisplayEntries(level) {
    const tableEntries = Array.isArray(level?.table_entries) ? level.table_entries : [];
    if (tableEntries.length) {
        return tableEntries
            .map((entry) => ({
                table_name: String(entry?.table_name || '').trim(),
                theme: String(entry?.theme || '').trim(),
            }))
            .filter((entry) => entry.table_name);
    }

    return (Array.isArray(level?.tables) ? level.tables : [])
        .map((tableName) => ({
            table_name: String(tableName || '').trim(),
            theme: '',
        }))
        .filter((entry) => entry.table_name);
}

function syncLevelTableEntries(level) {
    const existingEntries = Array.isArray(level?.table_entries) ? level.table_entries : [];
    const byTable = new Map(
        existingEntries.map((entry) => [
            String(entry?.table_name || '').trim().toLowerCase(),
            {
                table_name: String(entry?.table_name || '').trim(),
                theme: String(entry?.theme || '').trim(),
            },
        ]).filter(([key, entry]) => key && entry.table_name)
    );

    const tables = Array.isArray(level?.tables) ? level.tables : [];
    const tableEntries = tables.map((tableName) => {
        const normalized = String(tableName || '').trim().toLowerCase();
        return byTable.get(normalized) || {
            table_name: String(tableName || '').trim(),
            theme: '',
        };
    }).filter((entry) => entry.table_name);

    return {
        ...level,
        table_entries: tableEntries,
    };
}

function SchemaMapperPriorityConfig({ priorities, onUpdate, showNotification }) {
    const normalizedConfig = useMemo(() => normalizeConfig(priorities), [priorities]);
    const [priorityConfig, setPriorityConfig] = useState(normalizedConfig);
    const [tableInputs, setTableInputs] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [prioritySortOrder, setPrioritySortOrder] = useState('desc');
    const [importWarnings, setImportWarnings] = useState([]);
    const fileInputRef = useRef(null);

    useEffect(() => {
        setPriorityConfig(normalizedConfig);
        setTableInputs({});
        setHasChanges(false);
        setImportWarnings([]);
    }, [normalizedConfig]);

    const markChanged = () => setHasChanges(true);

    const getNextLevelName = (system) => {
        const nextIndex = (priorityConfig?.[system]?.levels || []).length + 1;
        return `Level ${nextIndex}`;
    };

    const updateSystemLevels = (system, updater) => {
        setPriorityConfig((prev) => ({
            ...prev,
            [system]: {
                levels: (updater(prev?.[system]?.levels || []) || []).map(syncLevelTableEntries),
            },
        }));
        markChanged();
    };

    const handleAddLevel = (system) => {
        updateSystemLevels(system, (levels) => ([
            ...levels,
            { name: getNextLevelName(system), tables: [], priority: null },
        ]));
        setTableInputs({});
    };

    const handleRemoveLevel = (system, levelIndex) => {
        updateSystemLevels(system, (levels) => levels.filter((_, index) => index !== levelIndex));
        setTableInputs({});
    };

    const handleMoveLevel = (system, levelIndex, direction) => {
        updateSystemLevels(system, (levels) => {
            const targetIndex = levelIndex + direction;
            if (targetIndex < 0 || targetIndex >= levels.length) {
                return levels;
            }
            const next = [...levels];
            const [moved] = next.splice(levelIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next;
        });
        setTableInputs({});
    };

    const handleLevelNameChange = (system, levelIndex, value) => {
        updateSystemLevels(system, (levels) => levels.map((level, index) => (
            index === levelIndex
                ? { ...level, name: value }
                : level
        )));
    };

    const handleAddTable = (system, levelIndex) => {
        const inputKey = `${system}-${levelIndex}`;
        const tableName = String(tableInputs[inputKey] || '').trim();
        if (!tableName) return;

        updateSystemLevels(system, (levels) => levels.map((level, index) => {
            if (index !== levelIndex) return level;
            const exists = (level.tables || []).some((table) => table.toLowerCase() === tableName.toLowerCase());
            if (exists) return level;
            return {
                ...level,
                tables: [...(level.tables || []), tableName],
            };
        }));

        setTableInputs((prev) => ({
            ...prev,
            [inputKey]: '',
        }));
    };

    const handleRemoveTable = (system, levelIndex, tableName) => {
        updateSystemLevels(system, (levels) => levels.map((level, index) => (
            index === levelIndex
                ? { ...level, tables: (level.tables || []).filter((table) => table !== tableName) }
                : level
        )));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await updateSchemaMapperPriorities({
                ...priorityConfig,
                l2: {
                    levels: (priorityConfig?.l2?.levels || []).map(syncLevelTableEntries),
                },
                l3: {
                    levels: (priorityConfig?.l3?.levels || []).map(syncLevelTableEntries),
                },
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            setHasChanges(false);
            showNotification('Schema mapper priority tables saved');
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setPriorityConfig(normalizedConfig);
        setTableInputs({});
        setHasChanges(false);
        setImportWarnings([]);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        try {
            setImporting(true);
            const importedConfig = await importSchemaMapperPriorities(file, prioritySortOrder);
            setPriorityConfig((prev) => ({
                ...prev,
                l2: {
                    levels: normalizeLevels(importedConfig?.l2?.levels || []),
                },
                l3: {
                    levels: normalizeLevels(importedConfig?.l3?.levels || []),
                },
            }));
            setTableInputs({});
            setPrioritySortOrder(importedConfig?.sort_order || prioritySortOrder);
            setImportWarnings(importedConfig?.warnings || []);
            setHasChanges(true);
            showNotification(`Imported schema mapper priorities from ${file.name}`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to import priority workbook', 'error');
        } finally {
            setImporting(false);
        }
    };

    const handleApplyPriorityOrder = () => {
        const hasPriorityMetadata = ['l2', 'l3'].some((system) => (
            (priorityConfig?.[system]?.levels || []).some((level) => typeof level?.priority === 'number')
        ));

        if (!hasPriorityMetadata) {
            showNotification('No imported priority values available to sort', 'error');
            return;
        }

        setPriorityConfig((prev) => ({
            ...prev,
            l2: {
                levels: sortLevelsByPriority(prev?.l2?.levels || [], prioritySortOrder),
            },
            l3: {
                levels: sortLevelsByPriority(prev?.l3?.levels || [], prioritySortOrder),
            },
        }));
        markChanged();
        showNotification(`Reordered levels by priority (${prioritySortOrder.toUpperCase()})`);
    };

    const renderSystemPanel = (system, title) => {
        const levels = priorityConfig?.[system]?.levels || [];
        const tableCount = countTables(levels);
        return (
            <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                            {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Evaluate these levels in order. Stop at the first level that yields at least one threshold-qualified match.
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {levels.length} level{levels.length === 1 ? '' : 's'}, {tableCount} table{tableCount === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddLevel(system)}
                    >
                        Add Level
                    </Button>
                </Box>

                <Stack spacing={2}>
                    {levels.map((level, levelIndex) => {
                        const inputKey = `${system}-${levelIndex}`;
                        const displayEntries = getDisplayEntries(level);
                        return (
                            <Paper key={`${system}-${levelIndex}`} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                                    <TextField
                                        size="small"
                                        label="Level Name"
                                        value={level.name}
                                        onChange={(event) => handleLevelNameChange(system, levelIndex, event.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    {typeof level?.priority === 'number' && (
                                        <Chip
                                            size="small"
                                            label={`Priority ${level.priority}`}
                                            variant="outlined"
                                        />
                                    )}
                                    <IconButton
                                        size="small"
                                        onClick={() => handleMoveLevel(system, levelIndex, -1)}
                                        disabled={levelIndex === 0}
                                        aria-label={`Move ${level.name} up`}
                                    >
                                        <ArrowUpwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleMoveLevel(system, levelIndex, 1)}
                                        disabled={levelIndex === levels.length - 1}
                                        aria-label={`Move ${level.name} down`}
                                    >
                                        <ArrowDownwardIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveLevel(system, levelIndex)}
                                        aria-label={`Delete ${level.name}`}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Add Table"
                                        placeholder={system === 'l2' ? 'Enter L2 table name' : 'Enter L3 table name'}
                                        value={tableInputs[inputKey] || ''}
                                        onChange={(event) => {
                                            const value = event.target.value;
                                            setTableInputs((prev) => ({ ...prev, [inputKey]: value }));
                                        }}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                event.preventDefault();
                                                handleAddTable(system, levelIndex);
                                            }
                                        }}
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleAddTable(system, levelIndex)}
                                        disabled={!String(tableInputs[inputKey] || '').trim()}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.75,
                                        minHeight: 48,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: 'grey.50',
                                    }}
                                >
                                    {displayEntries.map(({ table_name: tableName, theme }) => (
                                        <Chip
                                            key={`${level.name}-${tableName}`}
                                            label={theme ? `${tableName} (${theme})` : tableName}
                                            size="small"
                                            onDelete={() => handleRemoveTable(system, levelIndex, tableName)}
                                        />
                                    ))}
                                    {!displayEntries.length && (
                                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                                            No tables added yet.
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        );
                    })}

                    {levels.length === 0 && (
                        <Alert severity="info">
                            No priority levels configured for {title}. If left empty, the mapper will later fall back to the normal all-table search.
                        </Alert>
                    )}
                </Stack>
            </Paper>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Schema Mapper Priority Tables
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {priorityConfig.description}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasChanges && (
                        <Button variant="outlined" color="inherit" onClick={handleReset}>
                            Reset
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click "Save Changes" to persist them.
                </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
                L2 and L3 are managed independently. During mapping, each system will check Level 1 first, then Level 2, and continue until it finds at least one valid match. If no configured level produces a match, the normal all-table fallback will be used.
            </Alert>

            <Alert severity="info" sx={{ mb: 3 }}>
                Upload an Excel file with `L2` and `L3` sheets. The importer uses `Physical name` as the actual table name, carries over `Theme` when present, groups rows by `Priority`, and builds levels in ascending or descending priority order.
            </Alert>

            {importWarnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {importWarnings.join(' ')}
                </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 3 }}>
                <TextField
                    select
                    size="small"
                    label="Priority Order"
                    value={prioritySortOrder}
                    onChange={(event) => setPrioritySortOrder(event.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value="desc">Descending (higher priority first)</MenuItem>
                    <MenuItem value="asc">Ascending (lower priority first)</MenuItem>
                </TextField>
                <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleUploadClick}
                    disabled={importing}
                >
                    {importing ? 'Importing...' : 'Upload L2/L3 File'}
                </Button>
                <Button
                    variant="outlined"
                    onClick={handleApplyPriorityOrder}
                >
                    Apply Order
                </Button>
                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleImportFile}
                />
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    {renderSystemPanel('l2', 'L2')}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderSystemPanel('l3', 'L3')}
                </Grid>
            </Grid>
        </Box>
    );
}

export default SchemaMapperPriorityConfig;
